import { useEffect, useRef, useState } from 'react'
import type { XYPosition } from '@xyflow/react'
import type { CanvasPlacementMode } from '../ProjectCanvas.types'
import { canvasTextSquare } from '../utils/canvasText'

interface UseTextDrawingOptions {
  placementMode: CanvasPlacementMode
  canvasElement: HTMLElement | null
  screenToFlowPosition: (p: { x: number; y: number }) => XYPosition
  /** Ab wie vielen Bildschirmpixeln aus dem Klick ein Zug wird — die Schwelle des Canvas. */
  clickThreshold: number
  /** Das Quadrat steht — der Canvas legt den Text an (Seite → Schriftgröße). */
  onDrawn: (square: { x: number; y: number; side: number }) => void
}

/**
 * **Aufziehen** eines **Texts** — ein Quadrat, dessen Seite die Schriftgröße
 * festlegt (CONTEXT.md, Text). Dieselbe Bauart wie `useAreaDrawing`: ein Zug
 * auf der Fläche bei scharfem Werkzeug; ein Klick ohne Zug bleibt ein Klick und
 * setzt in der gemerkten Größe ab (`handlePaneClick`).
 *
 * Anders als dort legt dieser Hook nichts selbst an: Er meldet das Quadrat,
 * und der Canvas legt den Text über denselben Weg an wie beim Klick — Lage,
 * Bereich, Schritt und Bearbeiten-Modus stehen damit an EINER Stelle.
 *
 * `square` ist das Quadrat für die Vorschau (in Canvas-Koordinaten), `drewRef`
 * der Merker, mit dem `handlePaneClick` den Klick nach dem Loslassen schluckt.
 */
export function useTextDrawing({
  placementMode, canvasElement, screenToFlowPosition, clickThreshold, onDrawn,
}: UseTextDrawingOptions) {
  const [square, setSquare] = useState<{ x: number; y: number; side: number } | null>(null)
  const drewRef = useRef(false)
  // Über ein Ref: Der Rückruf ändert sich mit den Vorgaben, die Zeigerlauscher
  // sollen deshalb nicht neu gebunden werden.
  const onDrawnRef = useRef(onDrawn)
  onDrawnRef.current = onDrawn

  useEffect(() => {
    if (placementMode !== 'text' || !canvasElement) return
    let origin: XYPosition | null = null
    let originClient: { x: number; y: number } | null = null
    let drawn: { x: number; y: number; side: number } | null = null

    const stop = () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleCancel)
    }
    const handleMove = (e: PointerEvent) => {
      if (!origin || !originClient) return
      if (!drawn && Math.hypot(e.clientX - originClient.x, e.clientY - originClient.y) <= clickThreshold) return
      drawn = canvasTextSquare(origin, screenToFlowPosition({ x: e.clientX, y: e.clientY }))
      setSquare(drawn)
    }
    const handleCancel = () => {
      stop()
      origin = null
      drawn = null
      setSquare(null)
    }
    const handleUp = () => {
      stop()
      const result = drawn
      origin = null
      drawn = null
      setSquare(null)
      if (!result) return
      // Der Klick, den der Browser gleich noch meldet, darf keinen zweiten legen.
      drewRef.current = true
      onDrawnRef.current(result)
    }
    const handleDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      const target = e.target as Element | null
      if (!target?.closest?.('.react-flow__pane')) return
      originClient = { x: e.clientX, y: e.clientY }
      origin = screenToFlowPosition(originClient)
      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
      window.addEventListener('pointercancel', handleCancel)
    }
    canvasElement.addEventListener('pointerdown', handleDown)
    return () => {
      canvasElement.removeEventListener('pointerdown', handleDown)
      stop()
      setSquare(null)
    }
  }, [placementMode, canvasElement, screenToFlowPosition, clickThreshold])

  return { square, drewRef }
}
